import { find } from "linkifyjs";
import { Controller, useForm } from "react-hook-form";

import { useEscapeKey } from "@hooks/useEscapeKey";
import { Input } from "@ui/FormElements";
import { BaseDialog } from "@ui/Dialogs";
import useDialogStore from "@store/useDialogStore";

export interface ILinkDialogProps {
  href?: string;
  text?: string;
  onSave: (data: ILinkDialogForm) => void;
}

interface ILinkDialogForm {
  href: string;
  text: string;
}

const URL_REGEX =
  /^([-a-zA-Z0-9@:%_+.~#?&//=]{2,256}\.[a-z]{2,63}\b(\/[-a-zA-Z0-9@:%_+.~#?&//=]*)?)$/i;

export function LinkDialog({ href, text, onSave }: ILinkDialogProps) {
  const { isOpen, closeDialog } = useDialogStore();

  const { control, formState, handleSubmit, reset } = useForm<ILinkDialogForm>({
    mode: "onSubmit",
    defaultValues: {
      href: href || "",
      text: text || "",
    },
    reValidateMode: "onChange",
  });

  useEscapeKey({ callback: closeDialog, enabled: isOpen });

  const handleSave = (data: ILinkDialogForm) => {
    const { href, text } = data;

    onSave({
      href: find(href)[0].href,
      text,
    });
    reset();
  };

  const handleDialogClose = () => {
    reset();
    closeDialog();
  };

  return (
    <BaseDialog
      title="Add Link"
      primaryButton={{
        label: "Save",
        callback: handleSubmit(handleSave) as VoidFunction,
        disabled: !formState.isValid,
      }}
      secondaryButton={{
        label: "Cancel",
        buttonTypeOverride: "CANCEL",
        callback: handleDialogClose,
      }}
    >
      <form className="w-full space-y-4 px-4">
        <Controller
          control={control}
          name="text"
          rules={{
            required: "Please enter a link text",
            validate: {
              notEmpty: (value) => value.trim() !== "" || "Text cannot be only spaces",
            },
          }}
          render={({ field: { value, onChange } }) => (
            <Input
              onKeyDown={(ev) => {
                if (ev.key === "Enter") {
                  ev.preventDefault();
                  const target = ev.target as HTMLElement;
                  const form = target.closest("form");
                  const href = form?.querySelector('input[name="href"]') as HTMLInputElement | null;
                  href?.focus();
                }
              }}
              name="text"
              isFullWidth
              label="Text"
              value={value}
              onChange={onChange}
            />
          )}
        />

        <Controller
          control={control}
          name="href"
          rules={{
            required: "Please enter a valid URL",
            pattern: URL_REGEX,
          }}
          render={({ field: { value, onChange } }) => (
            <Input
              onKeyDown={(ev) => {
                if (ev.key === "Enter") {
                  ev.preventDefault();
                  handleSubmit(handleSave)().catch(() => {});
                }
              }}
              name="href"
              isFullWidth
              label="Link"
              value={value}
              onChange={onChange}
            />
          )}
        />
      </form>
    </BaseDialog>
  );
}
